/**
 * Public signup landing page for invitation tokens.
 *
 * Therapist-first layout (default): therapist photo + name + qualifications +
 * short bio front and centre, with a small clinic-strip header.
 *
 * Falls back gracefully when:
 *  - assignedTherapistId is null (clinic-level invitation, awaiting assignment) →
 *    clinic-first layout with "your therapist will be assigned" copy.
 *  - service group's signupLayout = 'clinic_first' → clinic prominent.
 */

import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Stack, Typography, Avatar, Chip, Button, TextField,
  Alert, FormControlLabel, Checkbox, CircularProgress, Divider
} from '@mui/material';
import * as apiService from '../../services/api';
import api from '../../services/api';
import { TERMS_URL, PRIVACY_URL } from '../../config/legalLinks';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api';

export default function InviteSignupPage({ setToken }) {
  const { invite } = useParams();
  const navigate = useNavigate();

  const [loadingInv, setLoadingInv] = useState(true);
  const [error, setError] = useState(null);
  const [invitation, setInvitation] = useState(null);
  const [therapistProfile, setTherapistProfile] = useState(null);
  const [serviceGroup, setServiceGroup] = useState(null);

  // Form state
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [agreed, setAgreed] = useState(false);
  // AD-01: published consent copy for this invitation's service group. When it
  // exists, each document is agreed separately and a Consent record is written
  // straight after registration. When it does not, `agreed` is a plain
  // acknowledgement of the public terms and is NOT presented as a record.
  const [consentDocs, setConsentDocs] = useState([]);
  const [consentChecked, setConsentChecked] = useState({});
  const [expandedDoc, setExpandedDoc] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingInv(true);
      setError(null);
      try {
        const res = await fetch(`${API_BASE_URL}/invitations/validate/${invite}`);
        if (!res.ok) {
          const j = await res.json().catch(() => ({}));
          throw new Error(j.message || `Invitation invalid (status ${res.status})`);
        }
        const data = await res.json();
        if (cancelled) return;
        const inv = data.invitation || data;
        setInvitation(inv);

        // If the invitation has a therapist, fetch their public profile.
        const therapistId = inv.assignedTherapist?._id || inv.assignedTherapist || inv.assignedTherapistId;
        if (therapistId) {
          try {
            const tRes = await fetch(`${API_BASE_URL}/public/therapist-profile/${therapistId}`);
            if (tRes.ok) {
              const t = await tRes.json();
              if (cancelled) return;
              setTherapistProfile(t.profile);
              setServiceGroup(t.serviceGroup);
            }
          } catch (_) { /* fallback to clinic-first below */ }
        }

        // If we still don't have a service group (no therapist OR therapist's
        // public profile didn't include one), pull it from the invitation.
        if (!cancelled) {
          const sgFromInv = inv.serviceGroup || inv.serviceGroupId;
          if (sgFromInv && typeof sgFromInv === 'object' && !serviceGroup) {
            setServiceGroup({
              id: String(sgFromInv._id || sgFromInv.id),
              name: sgFromInv.name || '',
              branding: sgFromInv.branding || {}
            });
          }
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load invitation');
      } finally {
        if (!cancelled) setLoadingInv(false);
      }
    })();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invite]);

  // Load the published consent documents for the resolved service group.
  useEffect(() => {
    const sgId = serviceGroup?.id || serviceGroup?._id;
    if (!sgId) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/public/consent/documents?serviceGroupId=${sgId}`);
        if (!res.ok) return;
        const data = await res.json();
        if (cancelled) return;
        setConsentDocs(data.documents || []);
      } catch (_) {
        // Signup must still work if consent copy cannot be loaded; the portal
        // step blocks before any care begins.
      }
    })();
    return () => { cancelled = true; };
  }, [serviceGroup]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    if (consentDocs.length > 0) {
      const unticked = consentDocs.filter(d => !consentChecked[d.documentId]);
      if (unticked.length) {
        setError('Please read and agree to each document before continuing.');
        return;
      }
    } else if (!agreed) {
      setError('You must agree to the Terms of Service and Privacy Policy.');
      return;
    }
    if (password.length < 8) {
      setError('Password must be at least 8 characters.');
      return;
    }
    if (password !== confirm) {
      setError('Passwords do not match.');
      return;
    }
    setSubmitting(true);
    try {
      const data = await apiService.register({
        name: invitation.invitedName,
        email: invitation.invitedEmail,
        password,
        invitationToken: invite
      });
      if (data?.token) {
        localStorage.setItem('invitationToken', invite);

        // Write the consent records now, with the freshly issued token, so the
        // agreement just given is actual evidence rather than discarded local
        // state. The server stamps version, IP and user agent. If this fails,
        // the portal consent step still blocks before any care begins — the
        // account is never left both unconsented and unblocked.
        if (consentDocs.length > 0) {
          try {
            await api.post('/consent/accept', {
              documentIds: consentDocs.map(d => d.documentId),
              capturedAt: 'signup'
            }, data.token);
          } catch (consentErr) {
            console.error('Consent could not be recorded at signup:', consentErr);
          }
        }

        setToken(data.token);
        navigate('/auth/redirect', { replace: true });
      } else {
        setError('Sign-up did not return a session. Please try again.');
      }
    } catch (err) {
      setError(err?.response?.data?.message || err?.message || 'Sign-up failed');
    } finally {
      setSubmitting(false);
    }
  }

  if (loadingInv) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress /></Box>;
  }

  if (error && !invitation) {
    return (
      <Box sx={{ p: 4, maxWidth: 520, mx: 'auto' }}>
        <Alert severity="error">{error}</Alert>
        <Box sx={{ mt: 2 }}>
          <Button onClick={() => navigate('/login')}>Go to login</Button>
        </Box>
      </Box>
    );
  }

  if (!invitation) return null;

  const layout = serviceGroup?.branding?.signupLayout || 'therapist_first';
  const therapistFirst = layout !== 'clinic_first' && !!therapistProfile;
  const clinicName = serviceGroup?.name || '';
  const clinicLogo = serviceGroup?.branding?.logoUrl || '';
  const clinicHero = serviceGroup?.branding?.heroImageUrl || '';
  const clinicShortDescription = serviceGroup?.branding?.clinicShortDescription || '';

  const tDisplayName = (therapistProfile?.displayName ||
    [therapistProfile?.title, therapistProfile?.firstName, therapistProfile?.lastName].filter(Boolean).join(' ')) || '';

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'grey.100' }}>
      {/* Clinic strip header */}
      <Box sx={{ bgcolor: 'background.paper', borderBottom: '1px solid', borderColor: 'divider', px: 2, py: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
        {clinicLogo && <img src={clinicLogo} alt={clinicName} style={{ height: 28, width: 'auto' }} />}
        <Typography variant="subtitle1" fontWeight={600}>{clinicName}</Typography>
      </Box>

      <Box sx={{ maxWidth: 720, mx: 'auto', p: { xs: 2, sm: 4 } }}>
        {clinicHero && (
          <Box sx={{
            height: 160,
            borderRadius: 2,
            backgroundImage: `url(${clinicHero})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            mb: 2
          }} />
        )}

        {/* Hero — therapist-first or clinic-first */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            {therapistFirst ? (
              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={3} alignItems={{ sm: 'center' }}>
                <Avatar
                  src={therapistProfile?.profilePictureUrl || undefined}
                  sx={{ width: 128, height: 128, fontSize: 36 }}
                >
                  {(therapistProfile?.firstName || tDisplayName || '?').charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ flex: 1 }}>
                  <Typography variant="h5" fontWeight={700}>{tDisplayName}</Typography>
                  {therapistProfile?.pronouns && (
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{therapistProfile.pronouns}</Typography>
                  )}
                  {therapistProfile?.qualifications?.length > 0 && (
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      {therapistProfile.qualifications.join(' · ')}
                    </Typography>
                  )}
                  {therapistProfile?.shortBio && (
                    <Typography variant="body1" sx={{ mt: 1.5, fontStyle: 'italic' }}>
                      &ldquo;{therapistProfile.shortBio}&rdquo;
                    </Typography>
                  )}
                  {therapistProfile?.modalities?.length > 0 && (
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 1.5, gap: 0.5 }}>
                      {therapistProfile.modalities.map((m) => (
                        <Chip key={m} label={m} size="small" variant="outlined" />
                      ))}
                    </Stack>
                  )}
                </Box>
              </Stack>
            ) : (
              <Stack spacing={1} alignItems="center" sx={{ textAlign: 'center', py: 2 }}>
                {clinicLogo && <img src={clinicLogo} alt={clinicName} style={{ maxHeight: 80 }} />}
                <Typography variant="h5" fontWeight={700}>{clinicName}</Typography>
                {clinicShortDescription && (
                  <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 480 }}>
                    {clinicShortDescription}
                  </Typography>
                )}
                {!therapistProfile && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Your therapist will be assigned shortly after you create your account.
                  </Alert>
                )}
              </Stack>
            )}
          </CardContent>
        </Card>

        {/* What happens next */}
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>What happens next</Typography>
            <Stack spacing={0.5}>
              <Typography variant="body2">1. Create your account using the form below.</Typography>
              <Typography variant="body2">2. Complete any pre-session check-ins your therapist has assigned.</Typography>
              <Typography variant="body2">
                3. {therapistProfile ? `Meet ${tDisplayName} for your first session.` : 'Meet your therapist for your first session.'}
              </Typography>
            </Stack>
          </CardContent>
        </Card>

        {/* Sign-up form */}
        <Card variant="outlined">
          <CardContent>
            <Typography variant="h6" gutterBottom>Create your account</Typography>
            {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
            <form onSubmit={handleSubmit}>
              <Stack spacing={2}>
                <TextField label="Full name" value={invitation.invitedName || ''} disabled />
                <TextField label="Email" value={invitation.invitedEmail || ''} disabled />
                <TextField
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="new-password"
                />
                <TextField
                  label="Confirm password"
                  type="password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  required
                  autoComplete="new-password"
                />
                {consentDocs.length > 0 ? (
                  /* Published copy exists for this service group: agree to each
                     document separately and record it. A single box covering
                     three documents is faster and worth less — the hybrid-care
                     document carries the clinical weight. */
                  <Box>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>Please read and agree</Typography>
                    <Stack spacing={1}>
                      {consentDocs.map(doc => (
                        <Box key={doc.documentId} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1 }}>
                          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                            <FormControlLabel
                              control={
                                <Checkbox
                                  checked={!!consentChecked[doc.documentId]}
                                  onChange={(e) => setConsentChecked(c => ({ ...c, [doc.documentId]: e.target.checked }))}
                                />
                              }
                              label={<Typography variant="body2">I agree to the {doc.title.toLowerCase()}</Typography>}
                            />
                            <Button
                              size="small"
                              onClick={() => setExpandedDoc(expandedDoc === doc.documentId ? null : doc.documentId)}
                            >
                              {expandedDoc === doc.documentId ? 'Hide' : 'Read'}
                            </Button>
                          </Stack>
                          {expandedDoc === doc.documentId && (
                            <Box sx={{
                              maxHeight: 220, overflowY: 'auto', p: 1.5, mt: 1,
                              bgcolor: 'action.hover', borderRadius: 1,
                              whiteSpace: 'pre-wrap', fontSize: '0.85rem', lineHeight: 1.6
                            }}>
                              {doc.body}
                            </Box>
                          )}
                          <Typography variant="caption" color="text.secondary">Version {doc.version}</Typography>
                        </Box>
                      ))}
                    </Stack>
                  </Box>
                ) : (
                  /* No published copy for this group yet. This is an
                     acknowledgement of the public terms, not a consent record —
                     the wording says so, and the full consent step runs in the
                     portal before any care begins. */
                  <Box>
                    <FormControlLabel
                      control={<Checkbox checked={agreed} onChange={(e) => setAgreed(e.target.checked)} />}
                      label={
                        <Typography variant="body2">
                          I agree to the <a href={TERMS_URL} target="_blank" rel="noreferrer">Terms of Service</a> and{' '}
                          <a href={PRIVACY_URL} target="_blank" rel="noreferrer">Privacy Policy</a>.
                        </Typography>
                      }
                    />
                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', ml: 4 }}>
                      You will be asked to read and agree to the full consent forms for this programme
                      before it starts.
                    </Typography>
                  </Box>
                )}
                <Button type="submit" variant="contained" size="large" disabled={submitting}>
                  {submitting ? 'Creating account…' : 'Create account'}
                </Button>
              </Stack>
            </form>
            <Divider sx={{ my: 2 }} />
            <Typography variant="caption" color="text.secondary">
              Already have an account? <a href="/login">Sign in</a>
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
}
